import React, { useEffect, useMemo, useRef, useState } from 'react';
import { icons } from './icons';
import useStyles from './styles';

const defaultAttributes = { width: '32', height: '32' };
const defaultSvgState = {
  innerHtml: null,
  childrenConfigs: [],
  attributes: defaultAttributes
};

/** @type {import('./props.type').IconProps} */
export const iconDefaultProps = {
  name: undefined,
  width: 'auto',
  height: 'auto',
  color: undefined,
  hoverColor: undefined,
  opacity: undefined,
  className: undefined
};

/** @type {import("react").FC<import('./props.type').IconProps>} */
const Icon = ({
  name,
  width,
  height,
  color,
  hoverColor,
  opacity,
  className,
  onClick
}) => {
  /** */
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);
  /** */

  const rootRef = useRef(/** @type {HTMLElement} */ (null));
  // this ref is used to cancel the fetch promise success callback in case name prop changed
  const currentName = useRef(name);
  const [svgState, setSvgState] = useState(defaultSvgState);

  const styles = useStyles({
    width,
    height,
    color,
    svgChildrenConfigs: svgState.childrenConfigs,
    opacity,
    hoverColor
  });

  const _className = useMemo(
    () => `${styles.root} ${className || ''}`,
    [className]
  );

  useEffect(() => {
    // reset
    currentName.current = name;
    setSvgState(defaultSvgState);

    const icon = icons[name];

    if (icon) {
      /**
       * retrieve from cache
       * @type {Promise<string>}
       */
      let svgProm = Icon.getCachedSvgPromise(name);

      if (!svgProm) {
        svgProm = fetch(icon.url)
          .then((r) => r.text())
          .catch(() => {
            // unset in cache
            Icon.setCachedSvgPromise(name, undefined);
            return null;
          });
        //save in cache
        Icon.setCachedSvgPromise(name, svgProm);
      }

      svgProm.then((svg) => {
        if (svg && isMounted.current && name === currentName.current) {
          const div = document.createElement('div');
          div.innerHTML = svg;

          // set the new attributes
          const newAttributes = {};
          for (const ob of div.firstElementChild.attributes) {
            const camelCased = ob.name.replace(/(-|:)([a-z])/g, (g) =>
              g[1].toUpperCase()
            );
            newAttributes[camelCased] = ob.value;
          }

          // setting the state in last.
          // Set state calls are called immediately inside asynchronous operations, timeouts, and intervals.
          setSvgState({
            innerHtml: div.firstElementChild.innerHTML,
            childrenConfigs: icon.svgChildrenConfigs,
            attributes: newAttributes
          });
        }
      });
    }
  }, [name]);

  return (
    <svg
      ref={rootRef}
      className={_className}
      {...svgState.attributes}
      dangerouslySetInnerHTML={{ __html: svgState.innerHtml }}
      onClick={onClick}
    ></svg>
  );
};

Icon.defaultProps = iconDefaultProps;

/** static properties for caching */
Icon.cacheStore = {};
Icon.getCachedSvgPromise = (name) => Icon.cacheStore[name];
Icon.setCachedSvgPromise = (name, prom) => (Icon.cacheStore[name] = prom);
/** */

export default Icon;
